// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicketRow } from "@/types";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import StatsSection from "@/UI/components/StatsSection";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { formatDateTime, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; pending: number; contacted: number; solved: number };

// --- Constants ----------------------------------------------------------------------------------

/** "deleted" is the client's own hide flag — an admin moves a ticket between the working three. */
const STATUSES: TySupportStatus[] = ["pending", "contacted", "solved"];

// --- Logic --------------------------------------------------------------------------------------

export default function SupportScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");

    const table = useResourceTable<TySupportTicketRow, TyStats>({
        path: "support",
        rowsKey: "tickets",
        query: { search, status: status || tab },
    });

    const isEditable = canEdit("support");

    /** The support screen's own filter labels double as the status vocabulary — one set of words. */
    const label = (status: TySupportStatus): string =>
        ({
            pending: t.support.filterPending,
            contacted: t.support.filterContacted,
            solved: t.support.filterSolved,
            deleted: t.support.filterDeleted,
        })[status] ?? status;

    const options = () => STATUSES.map((one) => ({ value: one, label: label(one) }));

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.lists.support.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "pending", label: t.lists.support.statPending, value: formatNumber(table.stats.pending, lang), tone: "bad" as const },
            { key: "contacted", label: t.lists.support.statContacted, value: formatNumber(table.stats.contacted, lang), tone: "warn" as const },
            { key: "solved", label: t.lists.support.statSolved, value: formatNumber(table.stats.solved, lang), tone: "good" as const },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TySupportTicketRow>[]>(
        () => [
            {
                key: "title",
                label: t.cols.title,
                width: "minmax(180px, 1.5fr)",
                render: (row) => <span className="list-cell-strong">{row.title}</span>,
            },
            { key: "store", label: t.cols.store, width: "minmax(140px, 1fr)", render: (row) => <span>{row.store?.name ?? "—"}</span> },
            {
                key: "client",
                label: t.cols.client,
                width: "minmax(140px, 1fr)",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.client?.name ?? "—"}</span>,
            },
            {
                key: "status",
                label: t.cols.status,
                width: "150px",
                render: (row) => <span>{label(row.status)}</span>,
                edit: isEditable ? { kind: "select", options: options(), value: (row) => row.status } : undefined,
            },
            {
                key: "createdAt",
                label: t.cols.created,
                width: "140px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDateTime(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang, isEditable]
    );

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-ticket");
        addComp({
            name: "confirm-delete-ticket",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.lists.support.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.lists.support.tabAll },
                    { value: "open", label: t.lists.support.tabOpen },
                    { value: "solved", label: t.lists.support.tabSolved },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.lists.support.search }}
                activeFilterCount={status ? 1 : 0}
                onClearFilters={() => setStatus("")}
                filters={<FilterSelect label={t.cols.status} value={status} onChange={setStatus} options={options()} />}
            />

            <DataTable<TySupportTicketRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) =>
                    isEditable ? (
                        <button
                            type="button"
                            className="list-row-btn list-row-btn--danger"
                            onClick={() => confirmDelete([row._id], row.title)}
                            aria-label={t.table.deleteRow}
                        >
                            <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                        </button>
                    ) : null
                }
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[{ key: "status", label: t.cols.status, kind: "select", options: options() }]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
